import { create } from "zustand";

/**
 * Store de notifications transitoires (« toasts »), S12-8/S12-9.
 *
 * État UI cross-composant volontairement minimal (Zustand — cf. ADR-001
 * « Zustand uniquement si besoin de state UI cross-composants »). Les toasts
 * sont déclenchés depuis des call-sites variés (modale réconciliation,
 * mise à jour d'adresse dans le wizard) et affichés par un `ToastViewport`
 * monté une seule fois dans le layout. Pas de dépendance externe (pas de
 * `sonner`/`react-hot-toast`, garde-fou « pas de dép lourde »).
 *
 * Ce n'est PAS du server-state : aucune donnée API n'y transite, uniquement
 * un libellé à afficher quelques secondes. TanStack Query reste la source de
 * vérité des données serveur.
 */

export type ToastVariant = "success" | "error";

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastState {
  toasts: Toast[];
  /** Empile un toast et retourne son id (pour dismiss programmatique). */
  push: (message: string, variant?: ToastVariant) => number;
  /** Retire un toast par id (clic sur la croix ou expiration du timer). */
  dismiss: (id: number) => void;
}

let nextId = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, variant = "success") => {
    const id = ++nextId;
    set((state) => ({ toasts: [...state.toasts, { id, message, variant }] }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/**
 * Helper ergonomique pour les call-sites : `toast.success("…")` /
 * `toast.error("…")` sans avoir à passer par le sélecteur de store. Utilisable
 * hors composant React (callbacks de mutation, handlers async).
 */
export const toast = {
  success: (message: string): number =>
    useToastStore.getState().push(message, "success"),
  error: (message: string): number =>
    useToastStore.getState().push(message, "error"),
};
